import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgpSwitch, NgpSwitchThumb } from 'ng-primitives/switch';

@Component({
  selector: 'tc-switch',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgpSwitch, NgpSwitchThumb],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.css',
})
export class SwitchComponent implements ControlValueAccessor {
  readonly leftLabel = input('');
  readonly rightLabel = input('');
  readonly ariaLabel = input('');
  readonly disabled = input(false, {
    transform: (v: boolean | string) => v === true || v === '',
  });
  readonly checked = input<boolean | undefined>(undefined);
  readonly checkedChange = output<boolean>();

  protected readonly isChecked = signal(false);
  protected readonly isDisabled = signal(false);

  readonly isLabeled = computed(
    () => !!this.leftLabel().trim() || !!this.rightLabel().trim(),
  );

  constructor() {
    effect(() => {
      const external = this.checked();
      if (external !== undefined) {
        untracked(() => this.isChecked.set(external));
      }
    });
  }

  protected switchAriaLabel(): string {
    const custom = this.ariaLabel().trim();
    if (custom) return custom;

    const left = this.leftLabel().trim();
    const right = this.rightLabel().trim();
    if (left && right) return `${left} / ${right}`;
    return left || right || 'Přepínač';
  }

  writeValue(value: boolean | null): void {
    this.isChecked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled || this.disabled());
  }

  protected toggle(): void {
    if (this.isDisabled()) return;
    this.onCheckedChange(!this.isChecked());
  }

  protected onCheckedChange(value: boolean): void {
    this.isChecked.set(value);
    this.onChange(value);
    this.onTouched();
    this.checkedChange.emit(value);
  }

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;
}
